import { useEffect, useState } from 'react';

/**
 * Debounces a value by the given delay in milliseconds.
 * Returns the latest value only after the caller has stopped updating it
 * for `delay` ms — useful for search inputs, async validation, etc.
 *
 * @param value - The value to debounce.
 * @param delay - Debounce delay in milliseconds.
 * @returns The debounced value.
 */
export function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return (): void => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}
